import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ChartConfiguration } from 'chart.js';
import { Subscription, skip } from 'rxjs';

import { AlertService } from '../shared/alert/alert.service';
import { MonthService, monthShort } from '../shared/month/month.service';
import { ThemeService } from '../account/settings/services/theme.service';
import { cssColor } from '../../utils/theme-colors';
import { InvestmentsSummaryService, InvestmentRecords, summarizeMonth } from '../investments/dashboard/investments-summary.service';
import { BarData, PIE_OPTIONS, PieData, applyChartTheme, brl, byCategoryChart } from './finance-charts';
import { FinanceRecords, FinanceSummaryService } from './finance-summary.service';
import { FinanceDashboard, buildFinanceDashboard } from './dashboard/finance-dashboard';

// Dashboard of Finances: summary of the month, one card per screen and the charts of the old Overview
@Component({
  selector: 'app-finances',
  standalone: false,
  templateUrl: './finances.component.html',
  styleUrls: ['../shared/dashboard/dashboard.scss']
})
export class FinancesComponent implements OnInit, OnDestroy {
  dashboard: FinanceDashboard | null = null;
  // Net invested in the month (Investments), shown on the balance tile
  invested: number | null = null;
  isLoading = false;

  balanceChart: PieData | null = null;
  cardsChart: PieData | null = null;
  historyChart: BarData | null = null;
  pieOptions = PIE_OPTIONS;
  barOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: { callbacks: { label: ctx => `${ctx.dataset.label}: ${brl(ctx.parsed.y ?? 0)}` } }
    },
    scales: {
      x: { grid: { display: false } },
      y: { ticks: { callback: v => `R$ ${(Number(v) / 1000).toLocaleString('pt-BR')} mil` } }
    }
  };

  months = inject(MonthService);
  private finances = inject(FinanceSummaryService);
  private investments = inject(InvestmentsSummaryService);
  private themes = inject(ThemeService);
  private alert = inject(AlertService);
  private records: FinanceRecords | null = null;
  private investmentRecords: InvestmentRecords | null = null;
  private subscriptions: Subscription[] = [];

  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    try {
      // Read once; changing the month only recomputes
      [this.records, this.investmentRecords] = await Promise.all([
        this.finances.load(),
        this.investments.load().catch(error => {
          console.error('FinancesComponent -> investments', error);
          return null;
        })
      ]);
    } catch (error) {
      console.error('FinancesComponent -> load', error);
      this.alert.show('Load failed!', 'error');
    } finally {
      this.isLoading = false;
    }

    this.subscriptions.push(
      this.months.month$.subscribe(() => this.build()),
      // Charts are drawn on canvas, so they are rebuilt with the colors of the new theme
      this.themes.dark$().pipe(skip(1)).subscribe(() => this.buildCharts())
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  // Share of the incomes, like the percentages of the spreadsheet
  percent(value: number): number {
    const incomes = this.dashboard?.summary.totals.incomes ?? 0;
    return incomes ? value / incomes : 0;
  }

  private build(): void {
    if (!this.records) return;
    const month = this.months.month;
    this.dashboard = buildFinanceDashboard(this.records, month);
    this.invested = this.investmentRecords ? summarizeMonth(month, this.investmentRecords, null).totals.net : null;
    this.buildCharts();
  }

  private buildCharts(): void {
    const dashboard = this.dashboard;
    if (!dashboard) return;
    applyChartTheme();

    const { bills, expenses, balance, incomes } = dashboard.summary.totals;
    this.balanceChart = incomes || bills || expenses ? {
      labels: ['Contas', 'Saídas', 'Saldo'],
      // A negative balance has no slice
      datasets: [{
        data: [bills, expenses, Math.max(balance, 0)],
        backgroundColor: ['var(--app-bills)', 'var(--app-expenses)', 'var(--app-balance)'].map(cssColor),
        borderColor: cssColor('var(--app-surface)')
      }]
    } : null;

    this.cardsChart = byCategoryChart(dashboard.summary.cardItems);

    const history = dashboard.history;
    this.historyChart = history.some(h => h.incomes || h.spent) ? {
      labels: history.map(h => monthShort(h.month)),
      datasets: [
        { label: 'Receita', data: history.map(h => h.incomes), backgroundColor: cssColor('var(--app-income)'), borderRadius: 3 },
        { label: 'Contas + saídas', data: history.map(h => h.spent), backgroundColor: cssColor('var(--app-expenses)'), borderRadius: 3 }
      ]
    } : null;
  }
}
